import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useExternalSupplierStorefrontsControllerListMyOfferings,
  useExternalSupplierStorefrontsControllerBulkSetPrices,
  useExternalSupplierStorefrontsControllerBulkSetActive,
  useExternalSupplierStorefrontsControllerSetPrice,
  getExternalSupplierStorefrontsControllerListMyOfferingsQueryKey,
  getExternalSupplierStorefrontsControllerListMineQueryKey,
} from "@semo/api-contracts";
import type { BulkPriceResultDto } from "@semo/api-contracts";

export type OfferingState = "all" | "listed" | "unpriced" | "inactive";

export interface BulkPriceInput {
  mode: "copy_base" | "link" | "unlink" | "clear";
  markupBp: number;
  overwrite: boolean;
  dryRun: boolean;
}

/**
 * 몰을 축으로 본 «내 상품».
 *
 * 물품관리 표는 품목이 축이라 승인분이 수만 줄 사이에 흩어진다. 오피스디포 기준
 * 활성 단가 2만 건 중 몰에 올라간 건 수십 건이라 «표에서 찾기» 는 성립하지 않는다.
 * 그래서 목록을 서버에서 몰 기준으로 뒤집어 받는다.
 */
export function useMyStorefrontOfferings(storefrontId: string) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<OfferingState>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const query = useExternalSupplierStorefrontsControllerListMyOfferings(
    storefrontId,
    {
      state,
      q: search.trim() || undefined,
      limit: pageSize,
      offset: page * pageSize,
    },
    { query: { enabled: Boolean(storefrontId) } },
  );

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey:
          getExternalSupplierStorefrontsControllerListMyOfferingsQueryKey(
            storefrontId,
          ),
      }),
      // 「허용 N · 노출 M」 배지가 같이 움직인다
      queryClient.invalidateQueries({
        queryKey: getExternalSupplierStorefrontsControllerListMineQueryKey(),
      }),
    ]);
  };

  const bulkPrice = useExternalSupplierStorefrontsControllerBulkSetPrices({
    mutation: {
      onSuccess: async (res, vars) => {
        if (vars.data.dryRun !== false) return; // 미리보기는 조용히 값만 돌려준다
        const changed = (res?.data as BulkPriceResultDto | undefined)?.changed ?? 0;
        toast.success(`${changed.toLocaleString("ko-KR")}건의 몰 가격을 설정했습니다`);
        await invalidate();
      },
      onError: (e) => toast.error(e?.message ?? "일괄 설정에 실패했습니다"),
    },
  });

  const bulkActive = useExternalSupplierStorefrontsControllerBulkSetActive({
    mutation: {
      onSuccess: async (res, vars) => {
        const changed = res?.data?.changed ?? 0;
        toast.success(
          vars.data.isActive
            ? `${changed.toLocaleString("ko-KR")}건의 노출을 재개했습니다`
            : `${changed.toLocaleString("ko-KR")}건의 노출을 중지했습니다`,
        );
        await invalidate();
      },
      onError: (e) => toast.error(e?.message ?? "실행에 실패했습니다"),
    },
  });

  const setPrice = useExternalSupplierStorefrontsControllerSetPrice({
    mutation: {
      onSuccess: async () => {
        await invalidate();
      },
      onError: (e) => toast.error(e?.message ?? "단가 저장에 실패했습니다"),
    },
  });

  const data = query.data?.data;

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    counts: data?.counts ?? { all: 0, listed: 0, unpriced: 0, inactive: 0 },
    isLoading: query.isLoading,
    isFetching: query.isFetching,

    state,
    setState: (next: OfferingState) => {
      setState(next);
      setPage(0); // 결과 집합이 통째로 달라지므로 그 자리의 페이지 번호는 의미가 없다
    },
    search,
    setSearch: (next: string) => {
      setSearch(next);
      setPage(0);
    },
    page,
    setPage,
    pageSize,

    runBulkPrice: (input: BulkPriceInput) =>
      bulkPrice.mutateAsync({ storefrontId, data: input }),
    runBulkActive: (isActive: boolean) =>
      bulkActive.mutate({
        storefrontId,
        data: { isActive, dryRun: false },
      }),
    setItemPrice: (itemId: string, price: number) =>
      setPrice.mutate({ storefrontId, itemId, data: { price } }),

    isBulkPending: bulkPrice.isPending || bulkActive.isPending,
    invalidate,
  };
}
